import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { Injectable, InjectionToken, Injector, StaticProvider } from '@angular/core';

import { BehaviorSubject, filter, take } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseSidebar } from '@/app/shared/ui-common/sidebar/base-sidebar/base-sidebar.abstract';

export const SIDEBAR_DATA = new InjectionToken<any>('SIDEBAR_DATA_INJECTION_TOKEN');

export type SidebarData = { [key: string]: any };

export interface SidebarConfig {
  heading?: string;
  secondaryButtonLabel?: string;
  actionButtonLabel?: string;
  closeOnOutsideClick?: boolean;
}

export interface SidebarState {
  opened: boolean;
  config?: SidebarConfig;
  data?: SidebarData;
  portal?: ComponentPortal<BaseSidebar>;
  component?: ComponentType<BaseSidebar>;
  closeValue?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  private state$ = new BehaviorSubject<SidebarState>({
    opened: false,
    data: {},
  });

  sidebarState$ = this.state$.pipe(
    map((state) => {
      if (!state.opened) {
        return state;
      }

      const providers: Array<StaticProvider> = [];

      if (state.data) {
        providers.push({
          provide: SIDEBAR_DATA,
          useValue: state.data,
        });
      }

      const injector = Injector.create({
        providers,
      });

      if (state.component) {
        state.portal = new ComponentPortal(state.component, null, injector);
      }

      return state;
    }),
  );

  close(state: boolean = true) {
    this.state$.next({
      opened: false,
      closeValue: state,
    });
  }

  open(component: ComponentType<any>, config?: SidebarConfig, data?: SidebarData) {
    this.state$.next({
      component,
      config,
      opened: true,
      data,
    });

    const onClose$ = this.state$.pipe(
      filter((val) => !val.opened),
      map((val) => val.closeValue),
      take(1),
    );

    return {
      afterClosed() {
        return onClose$;
      },
    };
  }
}
